import { useState, useEffect, useCallback } from "react";
import { isEqual } from "lodash";
import { useSalesContext } from "../../SalesContext";
import { API_BASE_URL } from "../../../config/config";

interface FilterOption {
  id: string;
  label: string;
}

interface UseFilterSelectionsProps {
  filterOptions: {
    yearOptions: FilterOption[];
    monthOptions: FilterOption[];
    mrNameOptions: FilterOption[];
  };
  endpoint: string;
  isCallsFilter?: boolean;
}

const useFilterSelections = ({
  filterOptions,
  endpoint,
  isCallsFilter = false,
}: UseFilterSelectionsProps) => {
  const { newFilterdata, setNewFilterdata, setKpiData, setSecondaryKpiData } =
    useSalesContext();
  const [selectedYearIds, setSelectedYearIds] = useState<string[]>(
    newFilterdata?.years
      ? filterOptions.yearOptions
          .filter((option) => newFilterdata.years.includes(option.label))
          .map((option) => option.id)
      : []
  );
  const [selectedMonthIds, setSelectedMonthIds] = useState<string[]>(
    newFilterdata?.months
      ? filterOptions.monthOptions
          .filter((option) => newFilterdata.months.includes(option.label))
          .map((option) => option.id)
      : []
  );
  const [selectedMrIds, setSelectedMrIds] = useState<string[]>(
    newFilterdata?.user_id || []
  );
  const [error, setError] = useState<string | null>(null);

  const getLastSixMonths = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const today = new Date();
    const lastSixMonths: string[] = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      lastSixMonths.push(months[date.getMonth()]);
    }
    return lastSixMonths.sort();  };

  const handleYearFilterChange = useCallback((selectedIds: string[]) => {
    setSelectedYearIds((prev) =>
      isEqual(prev, selectedIds) ? prev : selectedIds
    );
  }, []);

  const handleMonthFilterChange = useCallback((selectedIds: string[]) => {
    setSelectedMonthIds((prev) =>
      isEqual(prev, selectedIds) ? prev : selectedIds
    );
  }, []);

  const handleMrFilterChange = useCallback((selectedIds: string[]) => {
    setSelectedMrIds((prev) =>
      isEqual(prev, selectedIds) ? prev : selectedIds
    );
  }, []);

  useEffect(() => {
    const areAllFiltersEmpty =
      selectedYearIds.length === 0 &&
      selectedMonthIds.length === 0 &&
      selectedMrIds.length === 0;

    if (!areAllFiltersEmpty) {
      const newData = {
        months: selectedMonthIds
          .map(
            (id) =>
              filterOptions.monthOptions.find((option) => option.id === id)
                ?.label || ""
          )
          .filter(Boolean),
        years: selectedYearIds
          .map(
            (id) =>
              filterOptions.yearOptions.find((option) => option.id === id)
                ?.label || ""
          )
          .filter(Boolean),
        user_id: selectedMrIds,
      };

      setNewFilterdata((prev) => (isEqual(prev, newData) ? prev : newData));

      const fetchPerformanceData = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newData),
          });

          if (!response.ok) {
            throw new Error(
              `Failed to fetch performance data from ${endpoint}`
            );
          }

          const performanceData = await response.json();
          setKpiData(performanceData);
          setError(null);
        } catch (err) {
          setError(err.message);
          console.error(`API Error (${endpoint}):`, err.message);
        }
      };

      const fetchLastSixMonthsData = async () => {
        if (!isCallsFilter) return;

        const lastSixMonthsData = {
          months: getLastSixMonths(),
          years: newData.years,
          user_id: newData.user_id,
        };

        try {
          const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(lastSixMonthsData),
          });

          if (!response.ok) {
            throw new Error(
              "Failed to fetch performance coverage data (last 6 months)"
            );
          }

          const performanceData = await response.json();
          setSecondaryKpiData(performanceData);
          setError(null);
        } catch (err) {
          setError(err.message);
          console.error("API Error (last 6 months):", err.message);
        }
      };

      fetchPerformanceData();
      if (isCallsFilter) fetchLastSixMonthsData();
    }
  }, [
    selectedYearIds,
    selectedMonthIds,
    selectedMrIds,
    filterOptions,
    setNewFilterdata,
    setKpiData,
    setSecondaryKpiData,
    endpoint,
    isCallsFilter,
  ]);

  return {
    selectedYearIds,
    selectedMonthIds,
    selectedMrIds,
    handleYearFilterChange,
    handleMonthFilterChange,
    handleMrFilterChange,
    error,
  };
};

export default useFilterSelections;
